import React, { useState, useEffect } from "react";
import { GitBranch, Plus, CheckCircle2, Circle, FileDown, ChevronRight } from "lucide-react";
import { getDeals, createDeal, setDealStage, toggleDealTask, downloadVendorReport } from "../../api";
import { createAgreement, downloadAgreementPdf } from "../../api";
import { useToast } from "../../hooks/use-toast";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const STAGES = ["Offer Accepted", "Memo of Sale", "Searches", "Enquiries", "Mortgage Offer", "Exchange", "Completion"];
const fmt = (n) => `\u00A3${Number(n || 0).toLocaleString()}`;
const empty = { propertyTitle: "", buyerName: "", price: 400000 };

const AdminProgression = () => {
  const { toast } = useToast();
  const [deals, setDeals] = useState([]);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("active");
  const [form, setForm] = useState(empty);
  const refresh = () => getDeals().then(setDeals).catch(() => setDeals([]));
  useEffect(() => { refresh(); }, []);

  const isCompleted = (d) => d.stage === "Completion";
  const activeDeals = deals.filter((d) => !isCompleted(d));
  const soldDeals = deals.filter(isCompleted);
  const shown = tab === "active" ? activeDeals : soldDeals;

  const advance = async (id, stage) => {
    try { const u = await setDealStage(id, stage); setDeals((ds) => ds.map((d) => d.id === id ? u : d)); toast({ title: stage === "Completion" ? "Completed — property marked as sold" : `Moved to ${stage}` }); }
    catch { toast({ title: "Update failed" }); }
  };
  const toggle = async (dealId, taskId) => {
    try { const u = await toggleDealTask(dealId, taskId); setDeals((ds) => ds.map((d) => d.id === dealId ? u : d)); }
    catch { toast({ title: "Update failed" }); }
  };
  const submit = async (e) => {
    e.preventDefault();
    try { await createDeal({ ...form, price: Number(form.price) }); toast({ title: "Deal created" }); setOpen(false); setForm(empty); refresh(); }
    catch { toast({ title: "Failed to create deal" }); }
  };
  const input = "w-full h-[44px] px-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]";

  return (
    <div className="bg-white rounded-2xl border border-[#f0ece5] p-6" data-testid="admin-progression">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-display text-[20px] font-500 text-[#2b2b2b] flex items-center gap-2"><GitBranch size={19} className="text-[#b8935f]" /> Sales Progression ({deals.length})</h3>
        <button onClick={() => setOpen(true)} data-testid="new-deal-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#b8935f] text-white text-[13px] font-500 hover:bg-[#a67f4d] transition-colors"><Plus size={15} /> New Deal</button>
      </div>

      <div className="inline-flex rounded-full bg-[#faf7f2] border border-[#f0ece5] p-1 mb-5" data-testid="progression-tabs">
        <button onClick={() => setTab("active")} data-testid="progression-tab-active" className={`px-5 py-2 rounded-full text-[13px] font-500 transition-colors ${tab === "active" ? "bg-[#b8935f] text-white" : "text-[#6a6a6a]"}`}>Active ({activeDeals.length})</button>
        <button onClick={() => setTab("completed")} data-testid="progression-tab-completed" className={`px-5 py-2 rounded-full text-[13px] font-500 inline-flex items-center gap-1.5 transition-colors ${tab === "completed" ? "bg-green-600 text-white" : "text-[#6a6a6a]"}`}><CheckCircle2 size={14} /> Sold / Completed ({soldDeals.length})</button>
      </div>

      <div className="space-y-4">
        {shown.length === 0 && <p className="py-12 text-center text-[#a0a0a0] text-[14px]">{tab === "active" ? "No active deals in progression." : "No completed sales yet. Move a deal to Completion and it'll appear here."}</p>}
        {shown.map((d) => (
          <div key={d.id} className="p-5 rounded-xl bg-[#faf7f2] border border-[#f0ece5]" data-testid="deal-card">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
              <div>
                <p className="text-[15px] font-500 text-[#2b2b2b]">{d.propertyTitle}</p>
                <p className="text-[13px] text-[#8a8a8a]">{d.buyerName} · {fmt(d.price)}</p>
              </div>
              <div className="flex items-center gap-2">
                <Select value={d.stage} onValueChange={(v) => advance(d.id, v)}>
                  <SelectTrigger className="w-[170px] h-9 border-[#e2d8c8]" data-testid="deal-stage-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <button onClick={() => d.propertyId && downloadVendorReport(d.propertyId)} disabled={!d.propertyId} title="Vendor report PDF" className="w-9 h-9 rounded-lg border border-[#e2d8c8] flex items-center justify-center text-[#6a6a6a] hover:text-[#b8935f] hover:border-[#b8935f] transition-colors disabled:opacity-40"><FileDown size={15} /></button>
                <button onClick={async () => { const a = await createAgreement({ type: "sales-memo", propertyTitle: d.propertyTitle, partyA: "Vendor", partyB: d.buyerName, amount: d.price, terms: "Subject to contract." }); await downloadAgreementPdf(a.id); toast({ title: "Memorandum of Sale generated" }); }} data-testid="deal-memo-btn" title="Generate Memorandum of Sale" className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-[#e2d8c8] text-[12px] text-[#6a6a6a] hover:border-[#b8935f] hover:text-[#b8935f] transition-colors">Memo</button>
              </div>
            </div>
            {/* progress */}
            <div className="mb-3">
              <div className="flex items-center justify-between text-[11px] text-[#a0a0a0] mb-1"><span>{d.stage}</span><span data-testid="deal-progress">{d.progress}%</span></div>
              <div className="h-2 rounded-full bg-[#f0ece5] overflow-hidden"><div className="h-full bg-[#b8935f] transition-all duration-500" style={{ width: `${d.progress}%` }} /></div>
            </div>
            {/* tasks */}
            <div className="flex flex-wrap gap-2">
              {(d.tasks || []).map((t) => (
                <button key={t.id} onClick={() => toggle(d.id, t.id)} data-testid="deal-task" className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12px] border transition-colors ${t.done ? "bg-green-50 text-green-700 border-green-200" : "bg-white text-[#6a6a6a] border-[#e2d8c8] hover:border-[#b8935f]"}`}>
                  {t.done ? <CheckCircle2 size={13} /> : <Circle size={13} />} {t.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-[#a0a0a0] mt-3 flex items-center gap-1"><ChevronRight size={12} /> Next chase: {new Date(d.chaseDate).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</p>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogTitle className="sr-only">New Deal</DialogTitle>
          <h2 className="font-display text-[24px] font-300 text-[#2b2b2b] mb-4">New Deal</h2>
          <form onSubmit={submit} className="space-y-3">
            <input required placeholder="Property title" value={form.propertyTitle} onChange={(e) => setForm({ ...form, propertyTitle: e.target.value })} className={input} data-testid="deal-property-input" />
            <input required placeholder="Buyer name" value={form.buyerName} onChange={(e) => setForm({ ...form, buyerName: e.target.value })} className={input} data-testid="deal-buyer-input" />
            <input type="number" placeholder="Agreed price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={input} />
            <button type="submit" data-testid="submit-deal-btn" className="w-full h-[48px] rounded-lg bg-[#b8935f] text-white text-[15px] font-500 hover:bg-[#a67f4d] transition-colors">Create Deal</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminProgression;
